import React from 'react'
import { FilePreviewProvider } from './provider'
import { PreviewContent } from './previews'
import { Container } from './styles'
import FileMetadata from './FileMetadata'
import FileActions from './FileActions'
import { FilePreviewProps } from './types'

/**
 * FilePreview component displays a preview of a file with metadata and actions.
 * It supports rendering custom previews, metadata, and actions.
 * @param {FilePreviewProps} props - The properties for the FilePreview component.
 * @param {Object} props.file - Details like type, name, size, and URI.
 * @param {Object} [props.size] - Size of the preview container, default width 160.
 * @param {number} [props.aspectRatio=1] - Aspect ratio for the preview.
 * @param {boolean} [props.showMetadata=true] - Whether to show file metadata.
 * @param {function} [props.onClick] - Callback when the preview is clicked.
 * @param {function} [props.onDelete] - Callback when the delete action is triggered.
 * @param {string} [props.testID='FILE_PREVIEW'] - Test ID for the component.
 * @param {function} [props.renderActions] - Custom render function for actions.
 * @param {function} [props.renderMetadata] - Custom render function for metadata.
 * @param {function} [props.renderPreview] - Custom render function for the file preview.
 * @param {Object} [containerProps] - Additional props for the container.
 * @returns {JSX.Element} The rendered FilePreview component.
 */

function FilePreview({
  file,
  size = {
    width: 160,
  },
  aspectRatio = 1,
  showMetadata = true,
  onClick,
  onDelete,
  testID = 'FILE_PREVIEW',
  renderActions,
  renderMetadata,
  renderPreview,
  isLoading = false,
  style,
}: FilePreviewProps) {
  return (
    <FilePreviewProvider
      onClick={onClick}
      onDelete={onDelete}
      file={file}
      isLoading={isLoading}
      aspectRatio={aspectRatio}
      renderPreview={renderPreview}
    >
      <Container size={size} testID={testID} style={style}>
        <PreviewContent />
        {file && showMetadata && (renderMetadata?.(file) ?? <FileMetadata />)}
        {file && onDelete && (renderActions?.(file) ?? <FileActions />)}
      </Container>
    </FilePreviewProvider>
  )
}

export default FilePreview
